import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      nav("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to sign in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-aio-bg flex flex-col" data-testid="admin-login-page">
      <div className="border-b border-aio-border/60">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 h-16 flex items-center">
          <a href="/" className="flex items-baseline gap-3">
            <span className="font-display text-2xl tracking-tighter text-aio-ink">A.I.O</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-aio-soft">
              All In One Sourcing Specialists
            </span>
          </a>
          <span className="ml-auto text-[10px] uppercase tracking-[0.32em] text-aio-soft">
            Admin Console
          </span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:block relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1600&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative z-10 h-full p-16 flex flex-col justify-end text-white">
            <p className="text-[11px] uppercase tracking-[0.32em] text-white/70 mb-4">
              Sourcing Console
            </p>
            <h2 className="font-display text-5xl italic leading-tight max-w-lg">
              Every request, one specialist.
            </h2>
          </div>
        </div>

        <div className="flex items-center justify-center px-6 sm:px-12 py-16">
          <motion.form
            onSubmit={submit}
            data-testid="admin-login-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <div className="w-12 h-12 rounded-full bg-aio-olive text-aio-bg flex items-center justify-center mb-8">
              <Lock className="w-5 h-5" />
            </div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-aio-soft mb-4">
              Sign in
            </p>
            <h1 className="font-display text-5xl leading-tight tracking-tight text-aio-ink mb-10">
              Welcome back.
            </h1>

            <label className="block mb-8">
              <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">Email</span>
              <input
                data-testid="admin-email-input"
                type="text"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="editorial-input"
                placeholder="admin@aio.co"
              />
            </label>
            <label className="block mb-10">
              <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">Password</span>
              <input
                data-testid="admin-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <p
                data-testid="admin-login-error"
                className="mb-6 text-sm text-red-700 border-l-2 border-red-700/60 pl-3"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="inline-flex items-center gap-3 px-8 py-4 bg-aio-olive text-aio-bg hover:bg-aio-oliveDeep transition-colors text-sm uppercase tracking-[0.22em] disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Signing in" : "Sign in"}
              {!submitting && <span aria-hidden>&rarr;</span>}
            </button>

            <p className="mt-10 text-[11px] text-aio-soft/80 leading-relaxed max-w-sm">
              Access is restricted to A.I.O sourcing specialists. Please contact
              your administrator if you need credentials.
            </p>
          </motion.form>
        </div>
      </div>
    </div>
  );
}
